// Contrato de la pantalla Compromisos (`/collections/promises`). `PaymentPromiseRow` es la FILA que
// la pantalla pinta, no un DTO del silo: en el diseño el compromiso es una `collection_note` con
// `note_type = promesa` (06_COBRANZA §6.2/§6.3) y todavía no existe endpoint que lo devuelva.

/** Los cuatro canales por los que el cliente puede cumplir el compromiso (M4 §4.5). */
export type PromiseChannel = 'store' | 'app' | 'oxxo' | 'pcf-auto';

export const PROMISE_CHANNELS: readonly PromiseChannel[] = ['store', 'oxxo', 'app', 'pcf-auto'];

/** Ciclo del compromiso: activo → cumplido | incumplido. Aquí solo se pinta; el cron es de otra tarea. */
export type PromiseStatus = 'active' | 'kept' | 'broken';

/** Una entrada del histórico del caso: el contacto que el cobrador (o el sistema) registró. */
export interface PromiseCaseContact {
    /** Día de calendario `YYYY-MM-DD`. El histórico llega ya ordenado del más reciente al más viejo. */
    day: string;
    /** Por dónde se contactó: llamada, SMS, chat, WhatsApp. */
    channel: string;
    /** En qué terminó: no contesta, habló, promesa, enviado. */
    outcome: string;
    /** Texto libre del cobrador. Vacío = no dejó nota. */
    note: string;
}

/** Un compromiso de pago con lo mínimo del contexto del crédito para que el detalle no quede hueco. */
export interface PaymentPromiseRow {
    promiseId: string;
    creditId: string;
    customerId: string;
    customerName: string;
    /** Día prometido `YYYY-MM-DD`: la columna Fecha y todos los cortes por fecha. */
    promisedDay: string;
    promisedAmountCents: number;
    channel: PromiseChannel;
    status: PromiseStatus;
    /** Día en que el cobrador registró el compromiso. */
    registeredDay: string;
    /** Días de mora del crédito al día de hoy. `0` es al día. */
    arrearsDays: number;
    arrearsAmountCents: number;
    /** El histórico del caso, del más reciente al más viejo. Vacío = sin historial. */
    caseHistory: readonly PromiseCaseContact[];
}

/** Valor de filtro que no filtra nada. */
export const ANY_FILTER = 'all';
export type AnyFilter = typeof ANY_FILTER;

export interface PromisesFilters {
    /** Cliente, PR-XXXX o CR-XXXX. */
    search: string;
    channel: PromiseChannel | AnyFilter;
    /** Días de calendario `YYYY-MM-DD`; cadena vacía es «sin filtro». */
    fromDay: string;
    toDay: string;
    /** Rangos en CENTAVOS; `null` es «sin tope». */
    minAmountCents: number | null;
    maxAmountCents: number | null;
}

export function emptyPromisesFilters(): PromisesFilters {
    return {
        search: '',
        channel: ANY_FILTER,
        fromDay: '',
        toDay: '',
        minAmountCents: null,
        maxAmountCents: null,
    };
}

/** Las 5 pastillas de arriba de la tabla: además de contar, filtran. */
export type PromiseStatKey = 'all' | 'today' | 'tomorrow' | 'broken' | 'kept';

export const PROMISE_STAT_KEYS: readonly PromiseStatKey[] = [
    'all',
    'today',
    'tomorrow',
    'broken',
    'kept',
];

export type PromiseStats = Record<PromiseStatKey, number>;

/** Lo que dice la barra de herramientas: cuántos, cuánto suman y qué proporción se cumplió. */
export interface PromisesSummary {
    count: number;
    totalAmountCents: number;
    /** Cumplidos sobre cerrados (cumplidos + incumplidos) del set filtrado, en enteros de 0 a 100. */
    cureRatePct: number;
}
